import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  FiPlus,
  FiSearch,
  FiBriefcase,
  FiZap,
  FiList,
  FiTrendingUp,
  FiEdit2,
  FiCheck,
  FiX,
  FiUsers,
  FiBarChart2,
  FiEye,
  FiTrash2,
  FiCopy,
  FiExternalLink,
  FiFilter,
  FiChevronDown,
  FiChevronUp,
  FiCalendar,
  FiDownload,
  FiShield,
  FiActivity,
  FiCheckCircle,
} from "react-icons/fi";
import { FaCalendarAlt } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../index.css";
import "./EmployeeDashboard.css";
import "./EmployeeLeaves.css";

const API_BASE_URL = "https://ingrainhirebackend.ingrainsystems.com/api";

const ROLE_MAPPING = {
  Doctor: ["Consultant", "Medical Officer", "Physician"],
  "MERN Stack Developer": [
    "Web Developer",
    "Software Engineer",
    "Frontend Developer",
    "Backend Developer",
  ],
  "Product Specialist": [
    "Marketing Executive",
    "Sales",
    "Business Development",
  ],
  "Staff Nurse": ["Nursing Staff", "Nurse"],
};

const formatDate = (date) => {
  if (!date) return "-";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};


function JobPositionsStatus() {
  const [jobs, setJobs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingJobId, setEditingJobId] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [copiedId, setCopiedId] = useState("");

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");

  // Dropdown states & search
  const [roleSearchQuery, setRoleSearchQuery] = useState("");
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Dropdown refs
  const roleDropdownRef = useRef(null);
  const departmentDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);

  // Pagination & Modals
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [viewJob, setViewJob] = useState(null);

  const navigate = useNavigate();

  const getClientId = () => {
    return (
      localStorage.getItem("clientId") ||
      localStorage.getItem("clientCustomId") ||
      ""
    );
  };

  useEffect(() => {
    const clientId = getClientId();
    if (clientId) {
      fetchData();
    } else {
      setLoading(false);
    }

    const handleClickOutside = (event) => {
      if (
        roleDropdownRef.current &&
        !roleDropdownRef.current.contains(event.target)
      ) {
        setShowRoleDropdown(false);
      }
      if (
        departmentDropdownRef.current &&
        !departmentDropdownRef.current.contains(event.target)
      ) {
        setShowDepartmentDropdown(false);
      }
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setShowStatusDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchData = async () => {
    const clientId = getClientId();
    if (!clientId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Fetch Jobs
      const jobsRes = await axios.get(`${API_BASE_URL}/jobs/all/${clientId}`);
      if (jobsRes.data.success) {
        setJobs(jobsRes.data.jobPosts || []);
      } else {
        setJobs([]);
      }

      // Fetch Employees
      try {
        const empsRes = await axios.get(
          `${API_BASE_URL}/employees/get-employees/${clientId}`
        );
        if (empsRes.data.success || Array.isArray(empsRes.data)) {
          const empData = Array.isArray(empsRes.data)
            ? empsRes.data
            : empsRes.data.employees || empsRes.data.data || [];
          setEmployees(empData);
        } else {
          setEmployees([]);
        }
      } catch (empErr) {
        console.error("Failed to fetch employees:", empErr);
        setEmployees([]);
      }
    } catch (err) {
      console.error("Failed to fetch data:", err);
      setJobs([]);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const getEmployeeCount = (dept, role) => {
    if (!role) return 0;
    const roleAliases = [
      role.toLowerCase().trim(),
      ...(ROLE_MAPPING[role] || []).map((r) => r.toLowerCase()),
    ];

    return employees.filter((emp) => {
      const empDept = (emp.department || emp.dept || "").toLowerCase().trim();
      const empRole = (emp.role || emp.position || emp.designation || "")
        .toLowerCase()
        .trim();
      const empStatus = (emp.status || "").toLowerCase().trim();

      const deptMatch =
        dept && dept.toLowerCase() !== "general" && dept.toLowerCase() !== "all"
          ? empDept.includes(dept.toLowerCase().trim())
          : true;

      const roleMatch = roleAliases.some((alias) => empRole.includes(alias));
      return deptMatch && roleMatch && empStatus === "active";
    }).length;
  };

// Helper functions for tolerant search
const normalize = (str) => (str ? str.toLowerCase().replace(/\s+/g, "") : "");
const levenshtein = (a, b) => {
  const al = a.length, bl = b.length;
  if (al === 0) return bl;
  if (bl === 0) return al;
  const matrix = Array.from({ length: al + 1 }, () => new Array(bl + 1));
  for (let i = 0; i <= al; i++) matrix[i][0] = i;
  for (let j = 0; j <= bl; j++) matrix[0][j] = j;
  for (let i = 1; i <= al; i++) {
    for (let j = 1; j <= bl; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      );
    }
  }
  return matrix[al][bl];
};

  const startEditing = (job, field) => {
    setEditingJobId(job._id);
    setEditingField(field);
    setEditValue(job[field] === "General" ? "" : job[field] || "");
  };

  const cancelEditing = () => {
    setEditingJobId(null);
    setEditingField(null);
    setEditValue("");
  };

  const saveEdit = async (jobId) => {
    const clientId = getClientId();
    if (!clientId) return;

    setSaving(true);
    try {
      const updateData = {
        [editingField]:
          editingField === "vacancies" ? Number(editValue) : editValue,
        clientId: clientId,
      };
      const res = await axios.put(`${API_BASE_URL}/jobs/${jobId}`, updateData);
      if (res.data.success) {
        setJobs((prev) =>
          prev.map((j) => (j._id === jobId ? { ...j, ...updateData } : j))
        );
        toast.success("Position updated successfully!");
        setEditingJobId(null);
        setEditingField(null);
      } else {
        toast.error("Update failed: " + (res.data.message || "Unknown error"));
      }
    } catch (err) {
      console.error("Failed to update:", err);
      toast.error(
        "Update failed: " + (err.response?.data?.message || err.message)
      );
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (job) => {
    const fullLink = `${window.location.origin}/applying-job/${job._id}`;
    navigator.clipboard.writeText(fullLink);
    setCopiedId(job._id);
    toast.success("Application link copied to clipboard!");
    setTimeout(() => setCopiedId(""), 2200);
  };

  const getUniqueRoles = () => {
    const roles = jobs.map((job) => job.role).filter(Boolean);
    return Array.from(new Set(roles)).sort();
  };

  const getUniqueDepartments = () => {
    const depts = jobs.map((job) => job.department || "General").filter(Boolean);
    return Array.from(new Set(depts)).sort();
  };

  const clearFilters = () => {
    setSearchQuery("");
    setRoleFilter("");
    setDepartmentFilter("");
    setStatusFilter("");
    setDateFilter("");
    setRoleSearchQuery("");
    setCurrentPage(1);
  };

  const filteredJobs = jobs.filter((job) => {
    const query = searchQuery.toLowerCase();
    const matchesSearch =
      !query ||
      (job.role && job.role.toLowerCase().includes(query)) ||
      (job.department && job.department.toLowerCase().includes(query)) ||
      (job.skills && job.skills.toLowerCase().includes(query)) ||
      (job.location && job.location.toLowerCase().includes(query));

    const matchesRole = !roleFilter || job.role === roleFilter;
    const matchesDept =
      !departmentFilter || (job.department || "General") === departmentFilter;
    const matchesStatus = !statusFilter || (job.status || "active") === statusFilter;
    const matchesDate =
      !dateFilter ||
      new Date(job.createdAt || job.appliedAt).toISOString().slice(0, 10) ===
        dateFilter;

    return (
      matchesSearch && matchesRole && matchesDept && matchesStatus && matchesDate
    );
  });

  const totalPositions = jobs.reduce(
    (acc, job) => acc + (job.vacancies ?? 1),
    0
  );
  const totalActiveHired = jobs.reduce(
    (acc, job) => acc + getEmployeeCount(job.department, job.role),
    0
  );
  const overallFulfillment =
    totalPositions > 0
      ? Math.round((totalActiveHired / totalPositions) * 100)
      : 0;

  const totalPages = Math.ceil(filteredJobs.length / itemsPerPage) || 1;
  const paginatedJobs = filteredJobs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const downloadCSV = () => {
    if (filteredJobs.length === 0) {
      toast.info("No position records to download!");
      return;
    }

    const headers = [
      "Job ID",
      "Position Role",
      "Department",
      "Location",
      "Salary",
      "Active Workforce (Hired)",
      "Required Vacancies",
      "Fulfillment %",
      "Status",
      "Created Date",
    ];

    const csvRows = [
      headers.join(","),
      ...filteredJobs.map((job) => {
        const empCount = getEmployeeCount(job.department, job.role);
        const vacancies = job.vacancies ?? 1;
        const rate =
          vacancies > 0 ? Math.round((empCount / vacancies) * 100) : 0;
        return [
          `"${job._id || ""}"`,
          `"${(job.role || "").replace(/"/g, '""')}"`,
          `"${(job.department || "General").replace(/"/g, '""')}"`,
          `"${(job.location || "N/A").replace(/"/g, '""')}"`,
          `"${(job.salary || "-").replace(/"/g, '""')}"`,
          empCount,
          vacancies,
          `"${rate}%"`,
          `"${job.status || "active"}"`,
          `"${job.createdAt ? formatDate(job.createdAt) : "-"}"`,
        ].join(",");
      }),
    ];

    const csvData = csvRows.join("\n");
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `job_positions_status_${new Date()
      .toLocaleDateString("en-IN")
      .replace(/\//g, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getPeriodLabel = () => {
    return new Date().toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="emp-dash">
        <div className="emp-dash__loading">
          <div className="emp-dash__spinner" />
          <p className="emp-dash__loading-text">Loading positions status...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: "white" }} className="emp-dash">
      <ToastContainer position="top-right" autoClose={2500} hideProgressBar={false} />
      <main>
        {/* Page Header matching JobPost.jsx */}
        <div className="emp-dash__header">
          <div className="flex items-baseline gap-3 flex-wrap">
            <h1 className="emp-dash__greeting text-lg sm:text-xl font-bold whitespace-nowrap">
              Positions <span>Status</span>
            </h1>
            <span className="text-xs text-slate-400 font-medium hidden sm:inline">
              Track vacancies, existing headcount, and team fulfillment.
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="emp-dash__date-pill">
              <FaCalendarAlt />
              <span>{getPeriodLabel()}</span>
            </div>
            <button
              type="button"
              onClick={() => navigate("/post-new-job")}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold uppercase tracking-wider transition-all shadow-md shadow-indigo-600/20 cursor-pointer"
            >
              <FiPlus size={15} /> Post New Job
            </button>
          </div>
        </div>

        {/* Top KPI Stats Grid matching JobPost.jsx & AttendanceList */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4 mb-6">
          <div className="emp-dash__stat">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Total Positions</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--indigo">
                <FiBriefcase />
              </div>
            </div>
            <div className="emp-dash__stat-value">{jobs.length}</div>
            <div className="emp-dash__stat-meta">active job postings</div>
          </div>

          <div className="emp-dash__stat">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Planned Vacancies</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--emerald">
                <FiZap />
              </div>
            </div>
            <div className="emp-dash__stat-value text-emerald-700">
              {totalPositions}
            </div>
            <div className="emp-dash__stat-meta">target headcount required</div>
          </div>

          <div className="emp-dash__stat">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Active Workforce</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--rate">
                <FiUsers />
              </div>
            </div>
            <div className="emp-dash__stat-value text-indigo-700">
              {employees.length}
            </div>
            <div className="emp-dash__stat-meta">employees in directory</div>
          </div>

          <div className="emp-dash__stat">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Fulfillment Rate</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--violet">
                <FiTrendingUp />
              </div>
            </div>
            <div className="emp-dash__stat-value text-violet-700">
              {overallFulfillment}%
            </div>
            <div className="emp-dash__stat-meta">active / required ratio</div>
          </div>

          <div className="emp-dash__stat col-span-2 lg:col-span-1">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">System Health</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--rose">
                <FiActivity />
              </div>
            </div>
            <div className="emp-dash__stat-value text-slate-800">
              {jobs.filter((j) => (j.status || "active") === "active").length}
            </div>
            <div className="emp-dash__stat-meta">live accepting applications</div>
          </div>
        </div>

        {/* Filter Toolbar Card matching JobPost.jsx */}
        <div className="emp-dash__card p-4 mb-6">
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3">
            {/* Left: Search & Filter Dropdowns */}
            <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
              {/* Search Bar */}
              <div className="relative min-w-[200px] flex-1 max-w-[280px]">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                <input
                  type="text"
                  placeholder="Search Role, Skills, Location..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full pl-8 pr-3 py-2 text-xs border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white font-medium"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <FiX size={12} />
                  </button>
                )}
              </div>

              {/* Role Dropdown Filter */}
              <div className="relative" ref={roleDropdownRef}>
                <button
                  type="button"
                  onClick={() => setShowRoleDropdown((prev) => !prev)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl border transition-all bg-white whitespace-nowrap cursor-pointer ${
                    roleFilter
                      ? "border-indigo-500 text-indigo-700 bg-indigo-50"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <FiBriefcase className="text-gray-400 text-xs" />
                  <span className="truncate max-w-[120px]">
                    {roleFilter || "All Roles"}
                  </span>
                  <FiChevronDown size={11} className="text-gray-400" />
                </button>

                {showRoleDropdown && (
                  <div className="absolute z-50 mt-1.5 w-56 bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 overflow-y-auto p-1">
                    <div className="p-1.5 border-b border-gray-100 sticky top-0 bg-white">
                      <input
                        type="text"
                        placeholder="Search roles..."
                        value={roleSearchQuery}
                        onChange={(e) => setRoleSearchQuery(e.target.value)}
                        className="w-full py-1 px-2 text-xs bg-gray-50 border border-gray-200 rounded-lg outline-none focus:bg-white focus:border-indigo-600"
                        autoFocus
                      />
                    </div>
                    <div
                      onClick={() => {
                        setRoleFilter("");
                        setShowRoleDropdown(false);
                        setCurrentPage(1);
                      }}
                      className={`px-3 py-1.5 text-xs rounded-lg cursor-pointer transition-colors ${
                        !roleFilter
                          ? "bg-indigo-50 text-indigo-700 font-bold"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      All Roles
                    </div>
                    {getUniqueRoles()
                      .filter((r) =>
                        r.toLowerCase().includes(roleSearchQuery.toLowerCase())
                      )
                      .map((r) => (
                        <div
                          key={r}
                          onClick={() => {
                            setRoleFilter(r);
                            setShowRoleDropdown(false);
                            setCurrentPage(1);
                          }}
                          className={`px-3 py-1.5 text-xs rounded-lg cursor-pointer transition-colors ${
                            roleFilter === r
                              ? "bg-indigo-50 text-indigo-700 font-bold"
                              : "text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {r}
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* Department Dropdown Filter */}
              <div className="relative" ref={departmentDropdownRef}>
                <button
                  type="button"
                  onClick={() => setShowDepartmentDropdown((prev) => !prev)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl border transition-all bg-white whitespace-nowrap cursor-pointer ${
                    departmentFilter
                      ? "border-indigo-500 text-indigo-700 bg-indigo-50"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <FiList className="text-gray-400 text-xs" />
                  <span className="truncate max-w-[120px]">
                    {departmentFilter || "All Depts"}
                  </span>
                  <FiChevronDown size={11} className="text-gray-400" />
                </button>

                {showDepartmentDropdown && (
                  <div className="absolute z-50 mt-1.5 w-48 bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 overflow-y-auto p-1">
                    <div
                      onClick={() => {
                        setDepartmentFilter("");
                        setShowDepartmentDropdown(false);
                        setCurrentPage(1);
                      }}
                      className={`px-3 py-1.5 text-xs rounded-lg cursor-pointer transition-colors ${
                        !departmentFilter
                          ? "bg-indigo-50 text-indigo-700 font-bold"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      All Departments
                    </div>
                    {getUniqueDepartments().map((dept) => (
                      <div
                        key={dept}
                        onClick={() => {
                          setDepartmentFilter(dept);
                          setShowDepartmentDropdown(false);
                          setCurrentPage(1);
                        }}
                        className={`px-3 py-1.5 text-xs rounded-lg cursor-pointer transition-colors ${
                          departmentFilter === dept
                            ? "bg-indigo-50 text-indigo-700 font-bold"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {dept}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Status Filter Dropdown */}
              <div className="relative" ref={statusDropdownRef}>
                <button
                  type="button"
                  onClick={() => setShowStatusDropdown((prev) => !prev)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl border transition-all bg-white whitespace-nowrap cursor-pointer ${
                    statusFilter
                      ? "border-indigo-500 text-indigo-700 bg-indigo-50"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <FiActivity className="text-gray-400 text-xs" />
                  <span className="capitalize">{statusFilter || "All Status"}</span>
                  <FiChevronDown size={11} className="text-gray-400" />
                </button>

                {showStatusDropdown && (
                  <div className="absolute z-50 mt-1.5 w-36 bg-white border border-gray-200 rounded-xl shadow-xl p-1">
                    {["", "active", "draft"].map((st) => (
                      <div
                        key={st}
                        onClick={() => {
                          setStatusFilter(st);
                          setShowStatusDropdown(false);
                          setCurrentPage(1);
                        }}
                        className={`px-3 py-1.5 text-xs rounded-lg cursor-pointer capitalize transition-colors ${
                          statusFilter === st
                            ? "bg-indigo-50 text-indigo-700 font-bold"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {st || "All Status"}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Date Filter */}
              <div className="relative">
                <input
                  type="date"
                  value={dateFilter}
                  onChange={(e) => {
                    setDateFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="h-[34px] px-2.5 py-1 text-xs border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white font-medium cursor-pointer"
                />
              </div>
            </div>

            {/* Right: Export & Clear Actions */}
            <div className="flex items-center gap-2 flex-shrink-0 self-end lg:self-center">
              {(searchQuery ||
                roleFilter ||
                departmentFilter ||
                statusFilter ||
                dateFilter) && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="flex items-center gap-1 px-3 py-2 text-xs font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all whitespace-nowrap cursor-pointer"
                >
                  <FiX size={13} /> Reset
                </button>
              )}

              <button
                type="button"
                onClick={downloadCSV}
                className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all shadow-xs whitespace-nowrap cursor-pointer"
                title="Export positions report to CSV"
              >
                <FiDownload size={13} /> Export CSV
              </button>
            </div>
          </div>
        </div>

        {/* Positions Table Directory Card */}
        <div className="emp-dash__card p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                <FiBriefcase size={15} className="text-indigo-600" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-gray-800 uppercase tracking-widest">
                  Positions Directory
                </h2>
                <p className="text-[10px] text-gray-500">
                  Showing {paginatedJobs.length} of {filteredJobs.length} positions
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span className="font-medium">Rows per page:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 text-xs font-semibold outline-none focus:border-indigo-600"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto rounded-xl border border-gray-200/80">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-gray-50/90 border-b border-gray-200 text-gray-600 uppercase text-[10px] tracking-wider font-bold">
                  <th className="py-3.5 px-4 w-12 text-center">S.No</th>
                  <th className="py-3.5 px-4 min-w-[200px]">Position / Role</th>
                  <th className="py-3.5 px-4 min-w-[140px]">Department</th>
                  <th className="py-3.5 px-4 min-w-[120px]">Salary / Package</th>
                  <th className="py-3.5 px-4 min-w-[130px]">Location</th>
                  <th className="py-3.5 px-4 text-center min-w-[110px]">Workforce</th>
                  <th className="py-3.5 px-4 text-center min-w-[110px]">Vacancies</th>
                  <th className="py-3.5 px-4 text-center min-w-[120px]">Fulfillment</th>
                  <th className="py-3.5 px-4 text-center min-w-[90px]">Status</th>
                  <th className="py-3.5 px-4 text-center min-w-[130px]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredJobs.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-12 h-12 rounded-xl bg-gray-50 text-gray-300 flex items-center justify-center mb-2">
                          <FiBriefcase size={24} />
                        </div>
                        <p className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                          No Positions Found
                        </p>
                        <p className="text-[10px] text-gray-400 mt-1">
                          Try adjusting your search criteria or create a new job post.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedJobs.map((job, index) => {
                    const empCount = getEmployeeCount(job.department, job.role);
                    const vacancies = job.vacancies ?? 1;
                    const fillRate =
                      vacancies > 0
                        ? Math.round((empCount / vacancies) * 100)
                        : 0;

                    const isEditingDept =
                      editingJobId === job._id && editingField === "department";
                    const isEditingVacancies =
                      editingJobId === job._id && editingField === "vacancies";

                    return (
                      <tr
                        key={job._id}
                        className="hover:bg-indigo-50/20 transition-colors group"
                      >
                        {/* Index */}
                        <td className="py-3.5 px-4 text-center text-gray-400 font-medium text-[11px]">
                          {(currentPage - 1) * itemsPerPage + index + 1}
                        </td>

                        {/* Role */}
                        <td className="py-3.5 px-4">
                          <div className="flex items-start gap-2.5">
                            <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0 mt-0.5 shadow-xs">
                              <FiBriefcase size={14} />
                            </div>
                            <div className="min-w-0">
                              <p className="font-bold text-gray-900 text-xs truncate">
                                {job.role}
                              </p>
                              <span className="text-[10px] text-gray-400 font-medium">
                                Created: {formatDate(job.createdAt)}
                              </span>
                            </div>
                          </div>
                        </td>

                        {/* Department (Editable inline) */}
                        <td className="py-3.5 px-4">
                          {isEditingDept ? (
                            <div className="flex items-center gap-1">
                              <input
                                type="text"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                className="w-24 px-1.5 py-0.5 text-xs border border-indigo-500 rounded focus:ring-2 focus:ring-indigo-100 outline-none"
                                autoFocus
                                placeholder="Dept..."
                              />
                              <button
                                onClick={() => saveEdit(job._id)}
                                disabled={saving}
                                className="text-emerald-600 hover:text-emerald-700"
                              >
                                <FiCheck size={13} />
                              </button>
                              <button
                                onClick={cancelEditing}
                                className="text-rose-600 hover:text-rose-700"
                              >
                                <FiX size={13} />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 group/dept">
                              <span className="font-medium text-gray-700">
                                {job.department || "General"}
                              </span>
                              <button
                                type="button"
                                onClick={() => startEditing(job, "department")}
                                className="text-gray-300 hover:text-indigo-600 opacity-0 group-hover/dept:opacity-100 transition-opacity cursor-pointer"
                                title="Edit Department"
                              >
                                <FiEdit2 size={10} />
                              </button>
                            </div>
                          )}
                        </td>

                        {/* Salary */}
                        <td className="py-3.5 px-4 font-semibold text-emerald-700">
                          {job.salary || "-"}
                        </td>

                        {/* Location */}
                        <td className="py-3.5 px-4 text-gray-600 font-medium">
                          {job.location || "-"}
                        </td>

                        {/* Active Workforce */}
                        <td className="py-3.5 px-4 text-center">
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-100">
                            <FiUsers size={11} /> {empCount}
                          </span>
                        </td>

                        {/* Vacancies (Editable inline) */}
                        <td className="py-3.5 px-4 text-center">
                          {isEditingVacancies ? (
                            <div className="inline-flex items-center gap-1">
                              <input
                                type="text"
                                value={editValue}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (val === "" || /^\d+$/.test(val)) {
                                    setEditValue(val);
                                  }
                                }}
                                className="w-12 px-1.5 py-0.5 text-xs text-center border border-indigo-500 rounded focus:ring-2 focus:ring-indigo-100 outline-none font-bold"
                                autoFocus
                              />
                              <button
                                onClick={() => saveEdit(job._id)}
                                disabled={saving}
                                className="text-emerald-600 hover:text-emerald-700"
                              >
                                <FiCheck size={13} />
                              </button>
                              <button
                                onClick={cancelEditing}
                                className="text-rose-600 hover:text-rose-700"
                              >
                                <FiX size={13} />
                              </button>
                            </div>
                          ) : (
                            <div className="inline-flex items-center gap-1.5 group/vac">
                              <span className="font-bold text-gray-900 text-xs">
                                {vacancies}
                              </span>
                              <button
                                type="button"
                                onClick={() => startEditing(job, "vacancies")}
                                className="text-gray-300 hover:text-indigo-600 opacity-0 group-hover/vac:opacity-100 transition-opacity cursor-pointer"
                                title="Edit Vacancies"
                              >
                                <FiEdit2 size={10} />
                              </button>
                            </div>
                          )}
                        </td>

                        {/* Fulfillment % */}
                        <td className="py-3.5 px-4 text-center">
                          <div className="flex flex-col items-center gap-1">
                            <span
                              className={`text-xs font-extrabold ${
                                fillRate >= 100
                                  ? "text-emerald-700"
                                  : fillRate >= 50
                                  ? "text-indigo-700"
                                  : "text-rose-600"
                              }`}
                            >
                              {fillRate}%
                            </span>
                            <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all duration-300 ${
                                  fillRate >= 100
                                    ? "bg-emerald-500"
                                    : fillRate >= 50
                                    ? "bg-indigo-500"
                                    : "bg-rose-500"
                                }`}
                                style={{ width: `${Math.min(fillRate, 100)}%` }}
                              />
                            </div>
                          </div>
                        </td>

                        {/* Status */}
                        <td className="py-3.5 px-4 text-center">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                              job.status === "active"
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                : job.status === "draft"
                                ? "bg-amber-50 text-amber-700 border border-amber-200"
                                : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {job.status || "active"}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="py-3.5 px-4 text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => setViewJob(job)}
                              className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors cursor-pointer"
                              title="View Details"
                            >
                              <FiEye size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                navigate(`/post-new-job?editId=${job._id}`)
                              }
                              className="p-1.5 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer"
                              title="Edit Job Post"
                            >
                              <FiEdit2 size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() => copyToClipboard(job)}
                              className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors cursor-pointer"
                              title="Copy Apply Link"
                            >
                              {copiedId === job._id ? (
                                <FiCheck size={14} className="text-emerald-600" />
                              ) : (
                                <FiCopy size={14} />
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                navigate(
                                  `/post-new-job?role=${encodeURIComponent(
                                    job.role
                                  )}&department=${encodeURIComponent(
                                    job.department || "General"
                                  )}`
                                )
                              }
                              className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors cursor-pointer"
                              title="Post Similar Position"
                            >
                              <FiPlus size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
              <span className="text-xs text-gray-500">
                Page <strong className="text-gray-900">{currentPage}</strong> of{" "}
                <strong className="text-gray-900">{totalPages}</strong>
              </span>

              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-xs font-bold rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 cursor-pointer"
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => setCurrentPage(num)}
                    className={`w-7 h-7 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                      currentPage === num
                        ? "bg-indigo-600 text-white shadow-xs"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    {num}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    setCurrentPage((p) => Math.min(p + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-xs font-bold rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 cursor-pointer"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Quick Tip / Security Notice */}
        <div className="p-4 bg-amber-50 rounded-2xl border border-amber-100 flex items-start gap-3 mb-6">
          <FiZap size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-xs font-bold text-amber-900 uppercase tracking-wider mb-1">
              Workforce Fulfillment Tracking
            </h4>
            <p className="text-[11px] text-amber-800 leading-relaxed">
              Fulfillment rates automatically update based on active employee roles matching your job position requisitions. You can adjust vacancies or departments directly in the table cells above.
            </p>
          </div>
        </div>
      </main>

      {/* ============================================= */}
      {/* JOB DETAILS MODAL */}
      {/* ============================================= */}
      {viewJob && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="px-6 py-5 bg-gradient-to-r from-indigo-50 via-white to-violet-50 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center shadow-md shadow-indigo-600/20">
                  <FiBriefcase size={18} />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">
                    {viewJob.role}
                  </h2>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-indigo-600">
                    {viewJob.department || "General"} · {viewJob.location || "Remote"}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setViewJob(null)}
                className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center transition-colors cursor-pointer"
              >
                <FiX size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5 overflow-y-auto flex-1 custom-scrollbar">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">
                    Salary Package
                  </p>
                  <p className="text-xs font-bold text-emerald-700 mt-0.5">
                    {viewJob.salary || "Competitive"}
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">
                    Target Vacancies
                  </p>
                  <p className="text-xs font-bold text-gray-900 mt-0.5">
                    {viewJob.vacancies ?? 1} Openings
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">
                    Experience
                  </p>
                  <p className="text-xs font-bold text-gray-900 mt-0.5">
                    {viewJob.experience || "Fresher / Any"}
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">
                    Status
                  </p>
                  <p className="text-xs font-bold text-indigo-700 capitalize mt-0.5">
                    {viewJob.status || "active"}
                  </p>
                </div>
              </div>

              {/* Skills */}
              {viewJob.skills && (
                <div className="space-y-1.5">
                  <h3 className="text-xs font-bold text-gray-700 uppercase tracking-widest">
                    Required Skills
                  </h3>
                  <div className="flex flex-wrap gap-1.5">
                    {viewJob.skills.split(",").map((s, idx) => (
                      <span
                        key={idx}
                        className="px-2.5 py-1 rounded-lg text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100"
                      >
                        {s.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Description */}
              {viewJob.description && (
                <div className="space-y-1.5">
                  <h3 className="text-xs font-bold text-gray-700 uppercase tracking-widest">
                    Job Description & Context
                  </h3>
                  <p className="text-xs text-gray-600 whitespace-pre-wrap leading-relaxed p-3.5 bg-gray-50/70 rounded-xl border border-gray-100">
                    {viewJob.description}
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  copyToClipboard(viewJob);
                }}
                className="py-2 px-4 rounded-xl bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-1.5 cursor-pointer"
              >
                <FiCopy size={13} /> Copy Apply Link
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setViewJob(null)}
                  className="py-2 px-4 rounded-xl border border-gray-200 text-xs font-bold uppercase tracking-wider text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setViewJob(null);
                    navigate(`/post-new-job?editId=${viewJob._id}`);
                  }}
                  className="py-2 px-5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold uppercase tracking-wider transition-all shadow-md shadow-indigo-600/20 flex items-center gap-1.5 cursor-pointer"
                >
                  <FiEdit2 size={13} /> Edit Opening
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default JobPositionsStatus;